import {
  type IndicatorResult,
  type PineRunResponse,
  type Quote,
  formatNum,
  formatPrice,
  formatVolume,
  tfSeconds,
} from "@tt/shared";
import { MotionConfig, motion } from "motion/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { AppliedIndicators } from "./components/AppliedIndicators";
import { Brand } from "./components/Brand";
import { SymbolHeader } from "./components/SymbolHeader";
import { AuthScreen } from "./panels/AuthScreen";
import { Landing } from "./panels/Landing";
import { MarketOverview } from "./panels/MarketOverview";
import { MultiChart } from "./multichart/MultiChart";
import { FlashValue } from "./components/FlashValue";
import {
  Chart,
  type OverlayGroup,
  type PaneSpec,
  type SeriesSpec,
} from "./components/Chart";
import type { LegendControls } from "./components/IndicatorLegend";
import { Drawings } from "./components/Drawings";
import { EventMarkers } from "./components/EventMarkers";
import { IndicatorPicker } from "./components/IndicatorPicker";
import { LeftToolbar } from "./components/LeftToolbar";
import { ProfileButton } from "./components/ProfileButton";
import type { DrawingKind, DrawingManager } from "lightweight-charts-drawing";
import {
  TimeframePicker,
  type TimeframeAvailability,
} from "./components/TimeframePicker";
import { RangePicker } from "./components/RangePicker";
import { SymbolSearch } from "./components/SymbolSearch";
import { Popover } from "./components/Popover";
import { Watchlist } from "./components/Watchlist";
import { useAsync, usePersisted } from "./hooks/useApi";
import { useIndicators } from "./hooks/useIndicators";
import { type AuthUser, api } from "./lib/api";
import { STARTER_INDICATOR } from "./lib/pineLanguage";
import { BacktestPanel } from "./panels/BacktestPanel";
import { Admin } from "./panels/Admin";
import { OptionsResearch } from "./panels/OptionsResearch";
import { DataSources } from "./panels/DataSources";
import { NewsPanel } from "./panels/NewsPanel";
import { OptionsPanel } from "./panels/OptionsPanel";
import { PineDrawings } from "./components/PineDrawings";
import { PineEditor } from "./panels/PineEditor";
import { ResearchPanel } from "./panels/ResearchPanel";
import { SignalsPanel } from "./panels/SignalsPanel";

type Tab = "research" | "pine" | "signals" | "options" | "news";

const DEFAULT_WATCHLIST = [
  "AAPL",
  "MSFT",
  "NVDA",
  "AMZN",
  "GOOGL",
  "META",
  "TSLA",
  "SPY",
  "QQQ",
  "AMD",
];

/**
 * Legend label for one series.
 *
 * A single-plot indicator reads better with its settings than with its plot
 * name repeated — "EMA 21", not "EMA EMA". Multi-plot indicators need the plot
 * name to tell their lines apart.
 */
function seriesLabel(inst: IndicatorResult, plotName: string): string {
  if (inst.plots.length > 1) return `${inst.short} ${plotName}`;
  const numbers = Object.values(inst.params)
    .filter((v): v is number => typeof v === "number")
    .map((v) => (Number.isInteger(v) ? String(v) : String(v)));
  return numbers.length ? `${inst.short} ${numbers.join(" ")}` : inst.short;
}

/** Matches the timeframe segments, so the two controls feel like one system. */
const VIEW_SPRING = {
  type: "spring",
  stiffness: 520,
  damping: 38,
  mass: 0.7,
} as const;

export function App() {
  // Who is signed in, or null, or undefined while the first check is in
  // flight. The three states matter: rendering the sign-in screen during the
  // check would flash it at an already-authenticated user on every reload.
  const [me, setMe] = useState<AuthUser | null | undefined>(undefined);

  // A reset link arrives as /reset?token=..., which is the only route this
  // app has. Read once on mount and held, so clearing the URL below does not
  // also clear the token.
  const [resetToken] = useState<string | null>(() => {
    const p = new URLSearchParams(window.location.search);
    return window.location.pathname === "/reset" ? p.get("token") : null;
  });
  // null while the landing page is showing; the mode to open once the visitor
  // asks for the form.
  const [showAuth, setShowAuth] = useState<"login" | "signup" | null>(null);

  const [verifyToken] = useState<string | null>(() => {
    const p = new URLSearchParams(window.location.search);
    return window.location.pathname === "/verify" ? p.get("token") : null;
  });

  const refreshMe = useCallback(async () => {
    try {
      const { user } = await api.me();
      setMe(user);
    } catch {
      // 401 is the normal answer for a signed-out visitor, not an error.
      setMe(null);
    }
  }, []);

  useEffect(() => {
    void refreshMe();
  }, [refreshMe]);

  const [symbol, setSymbol] = usePersisted("tt.symbol", "AAPL");
  const [timeframe, setTimeframe] = usePersisted<string>("tt.timeframe", "1d");
  const [barCount, setBarCount] = usePersisted("tt.bars", 600);
  const [watchlist, setWatchlist] = usePersisted<string[]>(
    "tt.watchlist",
    DEFAULT_WATCHLIST,
  );
  const [tab, setTab] = usePersisted<Tab>("tt.tab", "research");
  // Strategy Tester dock: a panel between the chart and the drawer, matching
  // TradingView's Strategy Tester that appears below the chart whenever a
  // strategy is loaded. The user can collapse or close it; a close means
  // "not for this script" and does not fire again until the script changes.
  const [strategyDockOpen, setStrategyDockOpen] = usePersisted("tt.strat.dock", true);
  // Fullscreen covers the whole viewport, same semantics as the drawer's
  // own fullscreen -- useful when the tester's three panes compete with the
  // chart for vertical space on a laptop display.
  const [strategyDockFull, setStrategyDockFull] = useState(false);
  // Minimized collapses the dock to just its header bar so the main chart
  // can breathe without losing the current backtest results. Persisted so
  // the user's preferred default sticks across reloads.
  const [strategyDockMin, setStrategyDockMin] = usePersisted("tt.strat.min", false);
  const [drawerOpen, setDrawerOpen] = usePersisted("tt.drawer", true);
  const [pineSource, setPineSource] = usePersisted(
    "tt.pine.source",
    STARTER_INDICATOR,
  );
  const [pineResult, setPineResult] = useState<PineRunResponse | null>(null);
  // A script's plots hide and show like any indicator's. Without this the only
  // way to get a strategy off the chart was to delete the run and lose it.
  const [pineVisible, setPineVisible] = useState(true);

  // Auto re-run the Pine script when the user flips symbol, timeframe, or bar
  // count. Lives up here (rather than inside the PineEditor) so switching
  // from 1m to 1h re-renders the indicator even when the Pine tab is closed
  // -- without this, the chart's candles would reload at the new timeframe
  // but the old indicator's values would still be the ones computed on the
  // previous timeframe's bars.
  const pineAutoRunKey = useRef("");
  useEffect(() => {
    if (!pineResult || !pineSource.trim() || !symbol) return;
    const key = `${symbol}|${timeframe}|${barCount}|${pineSource.length}`;
    if (pineAutoRunKey.current === key) return;
    const prev = pineAutoRunKey.current;
    pineAutoRunKey.current = key;
    // On the very first render after a mount pineResult may already match
    // the current symbol/timeframe; skip the no-op refetch in that case.
    if (!prev) return;
    void (async () => {
      try {
        const next = await api.runPine({
          source: pineSource, symbol, timeframe, bars: barCount, inputs: {},
        });
        setPineResult(next);
      } catch {
        // Keep the previous result on screen so a transient fetch failure
        // doesn't blank the indicator; the user can hit Run manually to
        // retry.
      }
    })();
  }, [symbol, timeframe, barCount, pineSource, pineResult]);
  const [liveQuote, setLiveQuote] = useState<Quote | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);

  const indicators = useIndicators(symbol, timeframe, barCount);
  // Persisted so the layout a user settles on survives a reload; both are
  // per-browser preferences, not shared state.
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    // An explicit choice wins at any size. Without one, the default follows
    // the viewport: below 1100px the watchlist is an overlay, and opening it
    // over the chart on first load hides the thing the user came for.
    const stored = localStorage.getItem("tt.sidebar");
    if (stored !== null) return stored !== "0";
    return window.innerWidth >= 1100;
  });
  const [fullscreen, setFullscreen] = useState(false);
  // The drawer's own full screen, deliberately not persisted: a mode that
  // hides the chart and survives a reload would strand someone who forgot
  // they left it on.
  const [drawerFull, setDrawerFull] = useState(false);
  // Markets on first visit, chart after: the overview answers "where is the
  // market" and the chart answers "what is this symbol doing", and you have
  // to ask the first before the second is a question you can pose.
  type View = "markets" | "chart" | "workspace";

  const [view, setView] = useState<View>(() => {
    // A pop-out window carries ?view=workspace#tile=<id> so it opens
    // straight into the multi-chart view with the right tile. Honour that
    // first; only fall back to what the user had last.
    const params = new URLSearchParams(window.location.search);
    const fromUrl = params.get("view") as View | null;
    if (fromUrl === "workspace" || fromUrl === "chart" || fromUrl === "markets") {
      return fromUrl;
    }
    const saved = localStorage.getItem("tt.view") as View | null;
    return saved ?? "markets";
  });

  const goto = useCallback((next: View) => {
    setView(next);
    localStorage.setItem("tt.view", next);
  }, []);

  useEffect(() => {
    if (!fullscreen && !drawerFull && !strategyDockFull) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      setFullscreen(false);
      setDrawerFull(false);
      setStrategyDockFull(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [fullscreen, drawerFull, strategyDockFull]);
  // null is the cursor; anything else is a DrawingKind the library knows.
  const [tool, setTool] = useState<DrawingKind | null>(null);
  // TradingView's "Keep drawing": stay on the tool after placing one.
  const [stayInDrawingMode, setStayInDrawingMode] = usePersisted(
    "tt.keepDrawing",
    false,
  );
  const [drawingCount, setDrawingCount] = useState(0);
  /** Limitations the operator has accepted; they stay listed, uncounted. */
  const [acceptedGaps, setAcceptedGaps] = usePersisted<string[]>(
    "tt.gaps.accepted",
    [],
  );
  /**
   * Administration has its own address, so it can be bookmarked and opened
   * directly rather than only reached through the menu. Read once on mount,
   * like the reset and confirmation links above it.
   */
  const [adminOpen, setAdminOpen] = useState(
    () => window.location.pathname === "/admin",
  );
  const [researchOpen, setResearchOpen] = useState(
    () => window.location.pathname === "/research",
  );
  const showResearch = useCallback((on: boolean) => {
    setResearchOpen(on);
    if (on) window.history.pushState({ research: true }, "", "/research");
    else if (window.location.pathname === "/research") window.history.back();
  }, []);

  const showAdmin = useCallback((on: boolean) => {
    setAdminOpen(on);
    // pushState, not replace: closing with the browser's back button is what
    // anyone who opened it from a link will reach for first.
    if (on) window.history.pushState({ admin: true }, "", "/admin");
    else if (window.location.pathname === "/admin") window.history.back();
  }, []);

  // Someone who is not an operator and reaches /admin gets the app itself,
  // with the address corrected so a reload does not try again.
  useEffect(() => {
    if (!adminOpen || me === null || me === undefined || me.is_admin) return;
    setAdminOpen(false);
    window.history.replaceState({}, "", "/");
  }, [adminOpen, me]);

  // The back button leaves the page rather than leaving the app.
  useEffect(() => {
    const onPop = () => {
      setAdminOpen(window.location.pathname === "/admin");
      setResearchOpen(window.location.pathname === "/research");
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);
  const drawingMgr = useRef<DrawingManager | null>(null);
  const [magnet, setMagnet] = usePersisted("tt.magnet", true);
  const [volumeMode, setVolumeMode] = usePersisted<"off" | "overlay" | "pane">(
    "tt.volumeMode",
    "overlay",
  );
  const [extendedHours, setExtendedHours] = usePersisted(
    "tt.extendedHours",
    false,
  );
  // Light vs dark theme. Applied to the <html> element so one attribute
  // flips every CSS variable at once (both themes are declared in
  // styles.css) -- no component needs its own theme logic.
  const [theme, setTheme] = usePersisted<"light" | "dark">("tt.theme", "dark");
  useEffect(() => {
    const el = document.documentElement;
    if (theme === "light") el.setAttribute("data-theme", "light");
    else el.removeAttribute("data-theme");
  }, [theme]);
  const [eventKinds, setEventKinds] = usePersisted<Record<string, boolean>>(
    "tt.eventKinds",
    { earnings: true, dividend: true, split: true, news: true },
  );

  const bars = useAsync(
    (s) => api.bars(symbol, timeframe, barCount, extendedHours, s),
    [symbol, timeframe, barCount, extendedHours],
  );
  const health = useAsync((s) => api.health(s), []);
  const timeframeInfo = useAsync((s) => api.timeframes(s), []);
  // Company name and exchange. Cheap, cached server-side, and the only thing
  // that distinguishes a ticker you meant from one you mistyped.
  const detail = useAsync((s) => api.symbolDetail(symbol, s), [symbol]);

  const events = useAsync((s) => api.events(symbol, 1460, s), [symbol]);
  const sources = useAsync((s) => api.sources(s), []);
  const [sourcesOpen, setSourcesOpen] = useState(false);
  const sourcesAnchor = useRef<HTMLButtonElement>(null);

  const availability = useMemo(() => {
    const m = new Map<string, TimeframeAvailability>();
    for (const t of timeframeInfo.data?.timeframes ?? []) {
      m.set(t.id, {
        id: t.id,
        available: t.available,
        reason: t.reason,
        derived: t.derived,
        fetched_as: t.fetched_as,
      });
    }
    return m;
  }, [timeframeInfo.data]);

  /**
   * Live mode: today's session, following itself.
   *
   * Without it, reading today meant setting an intraday timeframe, choosing a
   * bar count that covers a session, scrolling to the right edge, and then
   * reloading by hand to see anything new -- four steps to answer "what is it
   * doing now". The button does all four and keeps doing the last one.
   */
  // Persisted, and so is what it replaced. Live mode writes the timeframe and
  // bar count, so if the mode itself were forgotten on reload you would come
  // back to five-minute bars with the button off and no way to tell why.
  const [live, setLive] = usePersisted("tt.live", false);
  const [beforeLiveSaved, setBeforeLiveSaved] = usePersisted<{
    timeframe: string;
    bars: number;
  } | null>("tt.live.prev", null);

  const toggleLive = useCallback(() => {
    if (!live) {
      setBeforeLiveSaved({ timeframe, bars: barCount });
      // 5m over ~2 days: enough bars to show the shape of today with
      // yesterday's close for context, which is what "the trend" means
      // before noon when today is only a few candles old.
      setTimeframe("5m");
      setBarCount(160);
      setLive(true);
      return;
    }
    if (beforeLiveSaved) {
      setTimeframe(beforeLiveSaved.timeframe);
      setBarCount(beforeLiveSaved.bars);
      setBeforeLiveSaved(null);
    }
    setLive(false);
  }, [
    live,
    timeframe,
    barCount,
    beforeLiveSaved,
    setTimeframe,
    setBarCount,
    setLive,
    setBeforeLiveSaved,
  ]);

  // Poll the quote so the header price is not stuck at the last bar's close.
  useEffect(() => {
    let alive = true;
    const ac = new AbortController();
    const tick = () =>
      api
        .quote(symbol, ac.signal)
        .then((q) => {
          if (alive) setLiveQuote(q);
        })
        .catch(() => {
          /* leave the previous quote showing */
        });
    void tick();
    const t = setInterval(tick, 15_000);
    return () => {
      alive = false;
      ac.abort();
      clearInterval(t);
    };
  }, [symbol]);

  /**
   * Refetch the bars while live mode is on.
   *
   * 15 seconds, the same cadence as the quote poll above: a 5m candle is only
   * finished every five minutes, but the one still forming moves the whole
   * time, and that last candle is the thing being watched.
   */
  useEffect(() => {
    if (!live) return;
    const t = setInterval(() => bars.refetch(), 15_000);
    return () => clearInterval(t);
    // bars.refetch is stable; listing bars would restart the timer on every
    // response, which is the one thing this must not do.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [live]);

  const pick = useCallback(
    (s: string) => {
      setSymbol(s);
      if (!watchlist.includes(s)) setWatchlist([s, ...watchlist].slice(0, 100));
    },
    [watchlist, setSymbol, setWatchlist],
  );

  // Results are only valid for the symbol and timeframe they ran on; showing
  // AAPL's RSI over NVDA's candles would be a quiet lie.
  const pineValid =
    pineResult !== null &&
    pineResult.symbol === symbol &&
    pineResult.timeframe === timeframe;

  const indicatorRun =
    indicators.run.data &&
    indicators.run.data.symbol === symbol &&
    indicators.run.data.timeframe === timeframe
      ? indicators.run.data
      : null;

  /** The controls the on-chart legend offers for one built-in indicator. */
  const controlsFor = useCallback(
    (
      instanceId: string,
      effectivePane: "overlay" | "separate",
    ): LegendControls => {
      const inst = indicators.instances.find(
        (i) => i.instance_id === instanceId,
      );
      const def = inst ? indicators.byId.get(inst.indicator_id) : undefined;
      const at = indicators.instances.findIndex(
        (i) => i.instance_id === instanceId,
      );
      return {
        visible: inst?.visible !== false,
        onToggleVisible: () => indicators.toggleVisible(instanceId),
        onRemove: () => indicators.remove(instanceId),
        onDuplicate: () => indicators.duplicate(instanceId),
        onMovePane: () =>
          indicators.setPane(
            instanceId,
            effectivePane === "overlay" ? "separate" : "overlay",
          ),
        movePaneLabel:
          effectivePane === "overlay" ? "Move to own pane" : "Move onto price",
        onMoveUp: at > 0 ? () => indicators.move(instanceId, -1) : undefined,
        onMoveDown:
          at >= 0 && at < indicators.instances.length - 1
            ? () => indicators.move(instanceId, 1)
            : undefined,
        settings:
          def && inst
            ? {
                def,
                params: inst.params,
                onChange: (patch) => indicators.update(instanceId, patch),
              }
            : undefined,
      };
    },
    [indicators],
  );

  /** A hidden indicator is still computed, so toggling it back is instant. */
  /** Controls for a Pine run's legend row.
   *
   * No settings entry: a script's parameters live in its source, so a gear
   * here would open a panel with nothing in it. Duplicate and move-pane are
   * absent for the same reason -- there is one run, and where it draws is
   * declared by `overlay` in the script itself. */
  const pineControls = useMemo<LegendControls>(
    () => ({
      visible: pineVisible,
      onToggleVisible: () => setPineVisible((v) => !v),
      onRemove: () => setPineResult(null),
    }),
    [pineVisible],
  );

  const isVisible = useCallback(
    (instanceId: string) =>
      indicators.instances.find((i) => i.instance_id === instanceId)
        ?.visible !== false,
    [indicators.instances],
  );

  /** Where an indicator actually draws, after any user override. */
  const paneOf = useCallback(
    (instanceId: string, fallback: "overlay" | "separate") =>
      indicators.instances.find((i) => i.instance_id === instanceId)
        ?.pane_override ?? fallback,
    [indicators.instances],
  );

  // Built-in indicators and a user's Pine script share the chart, so both feed
  // the same overlay groups and the same stack of panes.
  const overlayGroups = useMemo<OverlayGroup[]>(() => {
    const out: OverlayGroup[] = [];
    for (const inst of indicatorRun?.instances ?? []) {
      if (paneOf(inst.instance_id, inst.pane) !== "overlay") continue;
      const visible = isVisible(inst.instance_id);
      out.push({
        id: inst.instance_id,
        title: seriesLabel(inst, inst.plots[0]?.name ?? ""),
        // Hidden indicators contribute no series but keep their legend row,
        // which is what makes the eye a toggle rather than a delete.
        series: visible
          ? inst.plots.map((p) => ({
              key: `${inst.instance_id}:${p.name}`,
              name: p.name,
              values: p.values,
              color: p.color,
              kind: p.kind,
              linewidth: p.linewidth,
              // An oscillator dragged onto the price pane keeps its own scale;
              // an indicator that is natively a price overlay shares the axis.
              scaleId:
                inst.pane === "separate" ? `ov_${inst.instance_id}` : undefined,
            }))
          : [],
        controls: controlsFor(inst.instance_id, "overlay"),
      });
    }
    if (pineValid) {
      const series = pineResult!.plots
        .filter((p) => p.overlay && p.kind !== "shape")
        .map((p) => ({
          key: `pine:${p.id}`,
          name: p.title,
          values: p.values,
          color: p.color ?? "#4d8dff",
          kind: p.kind,
          linewidth: p.linewidth,
          colors: p.colors ?? null,
        }));
      if (series.length > 0) {
        out.push({
          id: "__pine_overlay__",
          title: String(pineResult!.declaration.title ?? "Script"),
          // Emptied rather than unmounted, so the legend row stays put and
          // reads "hidden" -- the same as a hidden indicator.
          series: pineVisible ? series : [],
          controls: pineControls,
        });
      }
    }
    return out;
  }, [
    indicatorRun,
    pineResult,
    pineValid,
    controlsFor,
    isVisible,
    paneOf,
    pineVisible,
    pineControls,
  ]);

  const panes = useMemo<PaneSpec[]>(() => {
    const out: PaneSpec[] = [];
    if (volumeMode === "pane" && bars.data) {
      const rows = bars.data.bars;
      out.push({
        id: "__volume__",
        title: "Volume",
        plots: [
          {
            key: "volume",
            name: "Volume",
            values: rows.map((b) => b.volume),
            color: "#4d8dff",
            kind: "histogram",
            linewidth: 1,
            colors: rows.map((b) =>
              b.close >= b.open ? "#26a96a99" : "#e5484d99",
            ),
          },
        ],
      });
    }
    for (const inst of indicatorRun?.instances ?? []) {
      if (paneOf(inst.instance_id, inst.pane) !== "separate") continue;
      const visible = isVisible(inst.instance_id);
      out.push({
        id: inst.instance_id,
        title: seriesLabel(inst, inst.plots[0]?.name ?? ""),
        plots: visible
          ? inst.plots.map((p) => ({
              key: `${inst.instance_id}:${p.name}`,
              name: p.name,
              values: p.values,
              color: p.color,
              kind: p.kind,
              linewidth: p.linewidth,
            }))
          : [],
        levels: visible
          ? inst.levels.map((l) => ({
              value: l.value,
              color: l.color,
              style: l.style,
              label: l.label,
            }))
          : [],
        scale: inst.scale,
        fills: inst.fills,
        controls: controlsFor(inst.instance_id, "separate"),
      });
    }
    if (pineValid) {
      const oscillators = pineResult!.plots.filter(
        (p) => !p.overlay && p.kind !== "shape",
      );
      if (oscillators.length > 0) {
        out.push({
          id: "__pine__",
          title: String(pineResult!.declaration.title ?? "Script"),
          controls: pineControls,
          plots: pineVisible
            ? oscillators.map((p) => ({
                key: `pine:${p.id}`,
                name: p.title,
                values: p.values,
                color: p.color ?? "#4d8dff",
                kind: p.kind,
                linewidth: p.linewidth,
              }))
            : [],
          levels: pineResult!.hlines
            .filter((h) => h.price !== null)
            .map((h) => ({
              value: h.price as number,
              color: h.color,
              style: h.style,
              label: h.title,
            })),
        });
      }
    }
    return out;
  }, [
    indicatorRun,
    pineResult,
    pineValid,
    controlsFor,
    isVisible,
    paneOf,
    volumeMode,
    bars.data,
    pineVisible,
    pineControls,
  ]);

  const shapes = useMemo<SeriesSpec[]>(() => {
    if (!pineValid) return [];
    return pineResult!.plots
      .filter((p) => p.kind === "shape")
      .map((p) => ({
        key: `pine:${p.id}`,
        name: p.title,
        values: p.values,
        color: p.color ?? "#2962ff",
        kind: p.kind,
        linewidth: p.linewidth,
        colors: p.colors ?? null,
      }));
  }, [pineResult, pineValid]);

  // Only a Pine script's own hlines belong on the price pane; a built-in
  // oscillator's levels live in its own pane.
  const priceLines = useMemo(() => {
    if (!pineValid || !pineResult!.declaration.overlay) return [];
    return pineResult!.hlines;
  }, [pineResult, pineValid]);

  const appliedCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const i of indicators.instances) {
      counts[i.indicator_id] = (counts[i.indicator_id] ?? 0) + 1;
    }
    return counts;
  }, [indicators.instances]);

  const quote = liveQuote;
  const chg = quote?.change_pct ?? null;
  const chgClass =
    chg === null ? "flat" : chg > 0 ? "up" : chg < 0 ? "down" : "flat";
  const last = bars.data?.bars.at(-1);
  const engine = health.data?.engine;

  // A confirmation or reset link opens its form before anything else --
  // ahead of the session probe, and regardless of its answer.
  //
  // This used to live inside the `me === null` branch below, so anyone
  // holding a live session cookie fell straight through to the app and the
  // reset form never appeared. That is precisely backwards: someone
  // resetting a password they believe is compromised is the least likely
  // person to have signed out first. The token in the URL is the intent,
  // and an existing session does not override it.
  if (resetToken || verifyToken) {
    return (
      <MotionConfig reducedMotion="user">
        <AuthScreen
          initialMode={verifyToken ? "verify" : "reset"}
          resetToken={resetToken}
          verifyToken={verifyToken}
          onSignedIn={() => {
            // Drop ?token= from the URL so a reset link cannot be replayed
            // out of history, and a refresh does not reopen the reset form.
            window.history.replaceState({}, "", "/");
            void refreshMe();
          }}
        />
      </MotionConfig>
    );
  }

  if (me === undefined) {
    // Nothing but the mark while the session check runs. A spinner would be
    // more honest about the wait but flashes on every reload for what is
    // usually a few milliseconds.
    return (
      <div className="auth-screen">
        <Brand size={30} />
      </div>
    );
  }

  if (me === null) {
    // Link tokens are handled above, so by here this is an ordinary visitor:
    // the landing page until they ask for a form.
    if (!showAuth) {
      return (
        <MotionConfig reducedMotion="user">
          <Landing
            onGetStarted={() => setShowAuth("signup")}
            onSignIn={() => setShowAuth("login")}
          />
        </MotionConfig>
      );
    }
    return (
      <MotionConfig reducedMotion="user">
        <AuthScreen
          initialMode={showAuth}
          resetToken={null}
          verifyToken={null}
          onSignedIn={() => {
            void refreshMe();
          }}
        />
      </MotionConfig>
    );
  }

  return (
    // reducedMotion="user" is the correct setting rather than "always": it
    // drops transform and layout animations -- the sliding timeframe pill, the
    // panel's 6px rise -- while keeping opacity ones. The tick flash is
    // opacity-only and carries information, so it survives, which is what a
    // user asking for less motion wants and not what "disable everything"
    // would give them.
    <MotionConfig reducedMotion="user">
      <div
        className={`app ${fullscreen ? "fullscreen" : ""} ${drawerFull ? "drawer-full" : ""}`}
      >
        <div className="topbar">
          <button
            className="icon-btn"
            title={sidebarOpen ? "Hide watchlist" : "Show watchlist"}
            aria-label={sidebarOpen ? "Hide watchlist" : "Show watchlist"}
            aria-expanded={sidebarOpen}
            onClick={() => {
              const next = !sidebarOpen;
              setSidebarOpen(next);
              localStorage.setItem("tt.sidebar", next ? "1" : "0");
            }}
          >
            <svg width="18" height="18" viewBox="0 0 15 15" aria-hidden="true">
              <path
                d="M2 3.5h11M2 7.5h11M2 11.5h11"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </button>
          <Brand size={20} className="brand" />

          <div className="seg view-seg">
            {(["markets", "chart", "workspace"] as const).map((v) => (
              <button
                key={v}
                className={`seg-item ${view === v ? "active" : ""}`}
                onClick={() => goto(v)}
              >
                {view === v && (
                  <motion.span
                    layoutId="view-pill"
                    className="seg-pill"
                    transition={VIEW_SPRING}
                  />
                )}
                <span className="seg-label">
                  {v === "markets" ? "Markets"
                    : v === "chart" ? "Chart"
                    : "Workspace"}
                </span>
              </button>
            ))}
          </div>
          <SymbolSearch onPick={(s) => {
            pick(s);
            // From Markets or Workspace, dropping a ticker in the search bar
            // means "take me to the chart", not "swap the symbol under the
            // overview". Chart is already where picking a chart does its
            // thing, so leave navigation alone there.
            if (view !== "chart") goto("chart");
          }} />
          <TimeframePicker
            value={timeframe}
            onChange={(tf) => setTimeframe(tf)}
            availability={availability}
          />
          <RangePicker
            timeframe={timeframe}
            value={barCount}
            onChange={setBarCount}
          />

          <span className="spacer" />

          {/* Operators only. These describe how this server is configured --
            which keys are set, what each missing one costs -- which is the
            deployer's business and nobody else's. A visitor who signs up
            should not be told the admin has not bought a Polygon key. */}
          {me.is_admin && (
            <button
              ref={sourcesAnchor}
              className="badge-button"
              onClick={() => setSourcesOpen((o) => !o)}
              title="What data sources are configured, and what each missing key changes"
            >
              {/* The operator sees the state either way, including the good one:
              "real-time" is the confirmation that the paid feed is actually
              live, which is worth being able to check at a glance rather than
              inferring from the absence of a warning. Everyone else sees none
              of this -- these describe the server, not the market. */}
              {!engine ? (
                <span className="badge bad status-badge">engine offline</span>
              ) : (
                <span
                  className={`badge status-badge ${
                    engine.data_quality.startsWith("delayed") ? "warn" : "good"
                  }`}
                  title={engine.data_quality}
                >
                  {engine.data_quality.startsWith("delayed")
                    ? "delayed data"
                    : "real-time"}
                </span>
              )}
              {/* One badge, not two. "news scoring off" was also counted inside
              "N limits", so the same single gap was reported twice and read as
              two separate problems. The count says how many, the tooltip says
              which, and the panel behind the button says what to do. */}
              {(() => {
                const open = (sources.data?.limitations ?? []).filter(
                  (l) => !acceptedGaps.includes(l.symptom),
                );
                if (open.length === 0) return null;
                return (
                  <span
                    className="badge warn status-badge"
                    title={open.map((l) => `• ${l.symptom}`).join("\n")}
                  >
                    {open.length}{" "}
                    {open.length === 1 ? "thing off" : "things off"}
                  </span>
                );
              })()}
            </button>
          )}

          <Popover
            anchorRef={sourcesAnchor}
            open={sourcesOpen}
            onClose={() => setSourcesOpen(false)}
            align="right"
            width={460}
          >
            {sources.data ? (
              <DataSources
                status={sources.data}
                dismissed={acceptedGaps}
                onDismiss={(symptom, hide) =>
                  setAcceptedGaps(
                    hide
                      ? [...acceptedGaps, symptom]
                      : acceptedGaps.filter((x) => x !== symptom),
                  )
                }
              />
            ) : (
              <div className="sources">
                <span className="spinner" /> loading…
              </div>
            )}
          </Popover>

          <button
            className="icon-btn"
            title={fullscreen ? "Exit full screen (Esc)" : "Full screen"}
            aria-label={fullscreen ? "Exit full screen" : "Full screen"}
            aria-pressed={fullscreen}
            onClick={() => {
              setFullscreen((v) => !v);
              setDrawerFull(false);
            }}
          >
            {fullscreen ? (
              <svg
                width="16"
                height="16"
                viewBox="0 0 14 14"
                aria-hidden="true"
              >
                <path
                  d="M5.5 1.5v4h-4M8.5 12.5v-4h4"
                  stroke="currentColor"
                  strokeWidth="1.4"
                  fill="none"
                  strokeLinecap="round"
                />
              </svg>
            ) : (
              <svg
                width="16"
                height="16"
                viewBox="0 0 14 14"
                aria-hidden="true"
              >
                <path
                  d="M1.5 5.5v-4h4M12.5 8.5v4h-4"
                  stroke="currentColor"
                  strokeWidth="1.4"
                  fill="none"
                  strokeLinecap="round"
                />
              </svg>
            )}
          </button>
        </div>

        {/* Over everything, not a third view: administration is a thing you step
          into and leave, not a place you read the market from. */}
        {adminOpen && me.is_admin && (
          <div className="admin-overlay">
            <Admin onClose={() => showAdmin(false)} />
          </div>
        )}

        {researchOpen && (
          <div className="admin-overlay">
            <OptionsResearch onClose={() => showResearch(false)} />
          </div>
        )}

        {view === "workspace" ? (
          <MultiChart />
        ) : view === "markets" ? (
          <MarketOverview
            onPick={(sym) => {
              pick(sym);
              goto("chart");
            }}
          />
        ) : (
          <div className={`body ${sidebarOpen ? "" : "no-sidebar"}`}>
            <LeftToolbar
              profile={
                <ProfileButton
                  user={me}
                  onSignOut={async () => {
                    await api.logout();
                    setMe(null);
                  }}
                  onAdmin={() => showAdmin(true)}
                  onResearch={() => showResearch(true)}
                  extendedHours={extendedHours}
                  onExtendedHours={setExtendedHours}
                  theme={theme}
                  onTheme={setTheme}
                />
              }
              tool={tool}
              onTool={setTool}
              stay={stayInDrawingMode}
              onStay={setStayInDrawingMode}
              magnet={magnet}
              onMagnet={setMagnet}
              count={drawingCount}
              onClear={() => drawingMgr.current?.clear()}
            />

            <div className="sidebar">
              <Watchlist
                symbols={watchlist}
                active={symbol}
                onPick={setSymbol}
                onRemove={(s) => setWatchlist(watchlist.filter((x) => x !== s))}
              />
            </div>

            <div className="main">
              <div className="chart-area">
                <SymbolHeader
                  symbol={symbol}
                  name={detail.data?.name}
                  exchange={detail.data?.exchange}
                  timeframe={timeframe}
                  price={quote?.price ?? last?.close ?? null}
                  change={quote?.change ?? null}
                  changePct={quote?.change_pct ?? null}
                  bid={quote?.bid}
                  ask={quote?.ask}
                  bar={last ?? null}
                  barSeconds={tfSeconds(timeframe)}
                >
                  {bars.data?.delayed && (
                    <span
                      className="badge warn"
                      title={bars.data.disclaimer ?? undefined}
                    >
                      delayed · {bars.data.provider}
                    </span>
                  )}
                  {events.data?.next_earnings && (
                    <span
                      className={`badge ${
                        events.data.next_earnings.days_away <= 7
                          ? "warn"
                          : "muted"
                      }`}
                      title={
                        `Consensus EPS ${events.data.next_earnings.eps_average ?? "—"}` +
                        ` (range ${events.data.next_earnings.eps_low ?? "—"}–` +
                        `${events.data.next_earnings.eps_high ?? "—"}). ` +
                        events.data.next_earnings.note
                      }
                    >
                      earnings in {events.data.next_earnings.days_away}d
                    </span>
                  )}
                  {pineResult &&
                    pineResult.symbol === symbol &&
                    pineResult.plots.length > 0 && (
                      <span className="badge muted">
                        {String(pineResult.declaration.title ?? "script")}{" "}
                        overlaid
                      </span>
                    )}
                </SymbolHeader>

                <AppliedIndicators
                  instances={indicators.instances}
                  byId={indicators.byId}
                  onRemove={indicators.remove}
                  onUpdate={indicators.update}
                  onToggleVisible={indicators.toggleVisible}
                  volumeMode={volumeMode}
                  onVolumeMode={setVolumeMode}
                  extendedHours={extendedHours}
                  onExtendedHours={setExtendedHours}
                  intraday={
                    (bars.data?.bars.length ?? 0) > 0 &&
                    timeframe !== "1d" &&
                    timeframe !== "1w" &&
                    timeframe !== "1M"
                  }
                  volumeNote={bars.data?.volume_note ?? null}
                  eventKinds={eventKinds}
                  onEventKinds={setEventKinds}
                  eventCounts={events.data?.counts ?? {}}
                  onAdd={() => setPickerOpen(true)}
                  onClear={indicators.clear}
                  busy={indicators.run.loading}
                />

                <div className="chart-host">
                  {bars.loading && !bars.data && (
                    <div className="empty">
                      <span className="spinner" /> loading {symbol}…
                    </div>
                  )}
                  {bars.error && (
                    <div className="err" style={{ margin: 14 }}>
                      {bars.error.message}
                    </div>
                  )}
                  {indicators.run.error && (
                    <div className="err" style={{ margin: 14 }}>
                      indicators: {indicators.run.error.message}
                    </div>
                  )}
                  {bars.data && bars.data.bars.length > 0 && (
                    <Chart
                      bars={bars.data.bars}
                      timeframe={timeframe}
                      caption={`${symbol} · ${timeframe}`}
                      overlayGroups={overlayGroups}
                      panes={panes}
                      shapes={shapes}
                      labels={
                        pineValid && pineVisible ? pineResult!.labels : []
                      }
                      tables={
                        pineValid && pineVisible ? pineResult!.tables : []
                      }
                      priceLines={priceLines}
                      volumeMode={volumeMode}
                      onRemovePane={(id) =>
                        id === "__pine__"
                          ? setPineResult(null)
                          : indicators.remove(id)
                      }
                      renderEventStrip={({ chart }) => (
                        <EventMarkers
                          chart={chart}
                          bars={bars.data?.bars ?? []}
                          events={events.data?.events ?? []}
                          enabled={eventKinds}
                        />
                      )}
                      viewKey={`${symbol}|${timeframe}|${extendedHours}`}
                      live={live}
                      onToggleLive={toggleLive}
                      renderPriceOverlay={({ chart, series }) => (
                        <>
                          {pineValid && pineVisible && (
                            <PineDrawings
                              chart={chart}
                              series={series}
                              barTimes={(bars.data?.bars ?? []).map(
                                (b) => b.time,
                              )}
                              lines={pineResult!.lines}
                              boxes={pineResult!.boxes}
                              polylines={pineResult!.polylines}
                              linefills={pineResult!.linefills}
                            />
                          )}
                          <Drawings
                            chart={chart}
                            series={series}
                            bars={bars.data?.bars ?? []}
                            symbol={symbol}
                            tool={tool}
                            magnet={magnet}
                            stayInDrawingMode={stayInDrawingMode}
                            onToolChange={setTool}
                            onManager={(m) => {
                              drawingMgr.current = m;
                              setDrawingCount(m?.drawings().length ?? 0);
                              m?.on("change", (ds) =>
                                setDrawingCount(ds.length),
                              );
                            }}
                          />
                        </>
                      )}
                    />
                  )}
                </div>
              </div>

              {/* Strategy Tester dock -- shown only when the Pine source is a
                  strategy, matching TradingView's behaviour. Collapsible
                  header, close button, body is the BacktestPanel. */}
              {pineResult?.declaration?.kind === "strategy" && strategyDockOpen && (
                <div className={`strat-dock ${strategyDockFull ? "strat-dock-full" : ""} ${strategyDockMin ? "strat-dock-min" : ""}`}>
                  {/* Minimal TradingView-style header: strategy name on the
                      left, window controls on the right -- no extra badge or
                      "Strategy Tester" chrome. */}
                  <div
                    className="strat-dock-head"
                    onDoubleClick={() => setStrategyDockMin(!strategyDockMin)}
                  >
                    <span className="strat-dock-title">
                      {String(pineResult.declaration.title ?? "Strategy")}
                    </span>
                    <div style={{ display: "inline-flex", gap: 6 }}>
                      <button
                        className="icon-btn small"
                        title={strategyDockMin ? "Expand Strategy Tester" : "Minimize Strategy Tester"}
                        aria-pressed={strategyDockMin}
                        onClick={() => {
                          // Minimizing out of fullscreen makes no sense -- the
                          // dock becomes a single bar covering the whole
                          // viewport. Exit fullscreen on the way down.
                          if (!strategyDockMin) setStrategyDockFull(false);
                          setStrategyDockMin(!strategyDockMin);
                        }}
                      >{strategyDockMin ? "▴" : "▾"}</button>
                      <button
                        className="icon-btn small"
                        title={strategyDockFull ? "Exit full screen (Esc)" : "Full screen"}
                        aria-pressed={strategyDockFull}
                        onClick={() => {
                          if (strategyDockMin) setStrategyDockMin(false);
                          setStrategyDockFull((v) => !v);
                        }}
                      >{strategyDockFull ? "⤡" : "⤢"}</button>
                      <button
                        className="icon-btn small"
                        title="Close Strategy Tester"
                        onClick={() => {
                          setStrategyDockFull(false);
                          setStrategyDockOpen(false);
                        }}
                      >×</button>
                    </div>
                  </div>
                  {!strategyDockMin && (
                    <div className="strat-dock-body">
                      <BacktestPanel
                        source={pineSource}
                        symbol={symbol}
                        timeframe={timeframe}
                      />
                    </div>
                  )}
                </div>
              )}

              <div className={`drawer ${drawerOpen ? "open" : "closed"}`}>
                <div className="tabs">
                  {(
                    [
                      "research",
                      "pine",
                      "signals",
                      "options",
                      "news",
                    ] as const
                  ).map((t) => (
                    <button
                      key={t}
                      className={`tab ${tab === t && drawerOpen ? "active" : ""}`}
                      onClick={() => {
                        setTab(t);
                        setDrawerOpen(true);
                      }}
                    >
                      {t === "pine"
                        ? "Pine editor"
                        : t === "signals"
                          ? "Signals"
                          : t === "options"
                            ? "Options"
                            : t === "news"
                              ? "News"
                              : "Research"}
                    </button>
                  ))}
                  <span className="spacer" />
                  <button
                    className="icon-btn small"
                    title={
                      drawerFull ? "Exit full screen (Esc)" : "Full screen"
                    }
                    aria-label={drawerFull ? "Exit full screen" : "Full screen"}
                    aria-pressed={drawerFull}
                    onClick={() => {
                      const next = !drawerFull;
                      setDrawerFull(next);
                      // Full screen on a collapsed drawer would show a 34px strip
                      // filling the window, so opening it is part of the gesture.
                      if (next) {
                        setDrawerOpen(true);
                        setFullscreen(false);
                      }
                    }}
                  >
                    {drawerFull ? (
                      <svg
                        width="15"
                        height="15"
                        viewBox="0 0 14 14"
                        aria-hidden="true"
                      >
                        <path
                          d="M5.5 1.5v4h-4M8.5 12.5v-4h4"
                          stroke="currentColor"
                          strokeWidth="1.4"
                          fill="none"
                          strokeLinecap="round"
                        />
                      </svg>
                    ) : (
                      <svg
                        width="15"
                        height="15"
                        viewBox="0 0 14 14"
                        aria-hidden="true"
                      >
                        <path
                          d="M1.5 5.5v-4h4M12.5 8.5v4h-4"
                          stroke="currentColor"
                          strokeWidth="1.4"
                          fill="none"
                          strokeLinecap="round"
                        />
                      </svg>
                    )}
                  </button>
                  <button
                    className="btn small"
                    onClick={() => {
                      setDrawerOpen(!drawerOpen);
                      // Collapsing out of full screen leaves nothing on screen at
                      // all, so the two come down together.
                      if (drawerOpen) setDrawerFull(false);
                    }}
                    title={drawerOpen ? "Collapse" : "Expand"}
                  >
                    {drawerOpen ? "▾" : "▴"}
                  </button>
                </div>

                {drawerOpen && (
                  // Keyed on the tab so the outgoing panel is replaced rather than
                  // mutated: a 6px rise and a fade says the content changed, which
                  // an instant swap between two dense tables does not. `mode="wait"`
                  // is deliberately not used -- holding an empty drawer for the
                  // exit duration is worse than a brief overlap.
                  <motion.div
                    key={tab}
                    className="panel-swap"
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.16, ease: [0.3, 0, 0.2, 1] }}
                  >
                    {tab === "research" && (
                      <ResearchPanel symbol={symbol} timeframe={timeframe} />
                    )}
                    {tab === "pine" && (
                      <PineEditor
                        symbol={symbol}
                        timeframe={timeframe}
                        bars={barCount}
                        source={pineSource}
                        onSourceChange={setPineSource}
                        onResult={setPineResult}
                        onOpenBacktest={() => setStrategyDockOpen(true)}
                      />
                    )}
                    {tab === "signals" && (
                      <SignalsPanel
                        symbols={watchlist}
                        timeframe={timeframe}
                        onPickSymbol={setSymbol}
                      />
                    )}
                    {tab === "options" && <OptionsPanel symbol={symbol} />}
                    {tab === "news" && (
                      <NewsPanel symbol={symbol} watchlist={watchlist} />
                    )}
                  </motion.div>
                )}
              </div>
            </div>
          </div>
        )}

        {pickerOpen && (
          <IndicatorPicker
            catalogue={indicators.catalogue.data?.indicators ?? []}
            applied={appliedCounts}
            onAdd={(id) => indicators.add(id)}
            onClose={() => setPickerOpen(false)}
          />
        )}
      </div>
    </MotionConfig>
  );
}
